import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { HomeModel } from "../home/types";
import Registration from "./index";
import { IamWrapper } from "iam-client";
import { Term } from "iam-client/dist/client/shared/termsOfServices";
import {
  OnEmailOtpRequiredApi,
  OnRegistrationRequiredApi,
} from "iam-client/dist/wrapper/registrationFlow";
import { OnTermsAndConditionsRetrievedApi } from "iam-client/dist/wrapper/shared";
import BasicAccountApplicationAPI from "../../api/commercial/basic-account-application";
import { LanguageContext } from "../../context/languageContext";
import { UserContext } from "../../context/userContext";
import { mapDataToCreateBasicAccountAppRequest } from "../shared/mappers";
import EmailOtp from "./email-otp";
import { RegistrationErrorStatus, RegistrationModel } from "./types";
import { LoadingContext } from "../../context/loaderContext";
import { ErrorContext } from "../../route/error/provider";
import GTM from "../../util/tracking/gtm";
import { ApplicationFlowSteps } from "../../route/application/authenticated/application-flow/types";
import { LanguageFrontendType } from "../shared/components/language/types";
import { IAMExceptionType } from "../../types";
import logging from "../../util/logging";
import { defaults } from "./account-form/defaults";
import RegistrationIamException from "./exception";
import { ProductContext } from "../../context/product-context/productContext";
import { Product } from "../../context/product-context/product";

const log = logging("registration flow");

interface RegistrationAdapterProps {
  homeModel: HomeModel;
  previous: () => void;
}

const RegistrationStep = {
  TERMS_OF_SERVICE_REQUEST: "TERMS_OF_SERVICE_REQUEST",
  CREATE_CREDENTIALS: "CREATE_CREDENTIALS",
  EMAIL_OTP: "EMAIL_OTP",
};

const RegistrationAdapter = (
  props: RegistrationAdapterProps,
): React.JSX.Element => {
  const { homeModel, previous } = props;
  const { selectedProduct } = useContext(ProductContext);
  const { language } = useContext(LanguageContext);
  const { fetchAuthenticatedUser } = useContext(UserContext);
  const [exception, setException] = useState<IAMExceptionType>(null);
  const { setError } = useContext(ErrorContext);
  const { setOverlayActive } = useContext(LoadingContext);

  const [terms, setTerms] = useState<Term[]>([]);
  const [registrationStep, setRegistrationStep] = useState(
    RegistrationStep.TERMS_OF_SERVICE_REQUEST,
  );
  // workaround for stale state in iam function handlers.
  const registrationModel = useRef<RegistrationModel>(defaults);
  const languageRef = useRef<LanguageFrontendType>(language);
  const selectedProductRef = useRef<Product>(selectedProduct);

  const [stepConfig, setStepConfig] = useState<
    | OnTermsAndConditionsRetrievedApi
    | OnRegistrationRequiredApi
    | OnEmailOtpRequiredApi
  >();
  const [formError, setFormError] = useState<unknown>(null);
  const [resetFlow, setResetFlow] = useState<boolean>(false);

  // shared 'context' between the components, let's construct it
  const context = useMemo(
    () => ({
      terms,
      selectedProduct,
      companyName: homeModel.isCompanyManuallyEntered
        ? homeModel.manuallyEnteredCompany.name
        : homeModel.selectedCompany?.name,
    }),
    [
      homeModel.isCompanyManuallyEntered,
      homeModel.manuallyEnteredCompany.name,
      homeModel.selectedCompany?.name,
      selectedProduct,
      terms,
    ],
  );

  const createBasicAccountApplication = async () => {
    await BasicAccountApplicationAPI.create(
      mapDataToCreateBasicAccountAppRequest(
        languageRef.current,
        selectedProductRef.current.productRef,
        homeModel.isCompanyManuallyEntered
          ? homeModel.manuallyEnteredCompany
          : homeModel.selectedCompany,
        registrationModel.current.admin,
        true,
      ),
    );
  };

  const onTermsAndConditionsRetrieved = (
    params: OnTermsAndConditionsRetrievedApi,
  ) => {
    log.info("Registration flow: T&C retrieved");
    setStepConfig(params);
    setRegistrationStep(RegistrationStep.TERMS_OF_SERVICE_REQUEST);
    setTerms(params.data.terms);
  };

  const onRegistrationRequired = (params: OnRegistrationRequiredApi) => {
    log.info("Registration flow: Registration required");
    params.proceed({
      email: registrationModel.current.admin.emailBusiness,
      password: registrationModel.current.password,
    });
    registrationModel.current.password = "";
    setFormError(params.errors);
  };

  const onEmailOtpRequired = (params: OnEmailOtpRequiredApi) => {
    log.info("Registration flow: OTP required");
    setStepConfig(params);
    setRegistrationStep(RegistrationStep.EMAIL_OTP);
    setFormError(params.errors);
    GTM.trackVirtualPageView(
      ApplicationFlowSteps.EMAIL_OTP,
      selectedProductRef.current.productRef,
      language,
    );
  };

  const onFinish = async () => {
    log.info("Registration flow: Finished");
    await createBasicAccountApplication();
    fetchAuthenticatedUser();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onError = (errors: any) => {
    log.error(`Registration flow: Errors ${JSON.stringify(errors)}`);
    const error = errors[0];
    if (error?.code === RegistrationErrorStatus.EMAIL_OTP_WRONG) {
      setException(IAMExceptionType.TOO_MANY_EMAIL_OTP_ATTEMPTS);
      return;
    }
    // general errors that can't be recovered.
    setError(errors);
  };

  const onLoadingChange = (loading: boolean) => {
    setOverlayActive(loading);
    log.info("Registration flow: Loading change " + loading);
  };

  useEffect(() => {
    const refreshTermsAndConditions = () => {
      if (stepConfig && "retrieveTermsOfServices" in stepConfig) {
        stepConfig.retrieveTermsOfServices();
      }
    };
    languageRef.current = language;
    refreshTermsAndConditions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    selectedProductRef.current = selectedProduct;
  }, [selectedProduct]);

  useEffect(() => {
    IamWrapper.init({
      resolveGenericErrorHandlers: () => ({
        on5XX: onError,
      }),
    });
    const startRegistration = async () => {
      IamWrapper.registrationFlow(
        {
          onInit: async ({ start }) => {
            await start();
          },
          onTermsAndConditionsRetrieved,
          onRegistrationRequired,
          onEmailOtpRequired,
          onFinish,
          onError,
          onLoadingChange,
        },
        {
          applicationId: "1fa_commercial_onboarding",
          autoLogin: true,
        },
      );
    };
    startRegistration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetFlow]);

  const handleOnExceptionExit = () => {
    setResetFlow(!resetFlow);
    setException(null);
    setRegistrationStep(RegistrationStep.TERMS_OF_SERVICE_REQUEST);
  };

  if (exception) {
    return (
      <RegistrationIamException
        exception={exception}
        handleOnClick={handleOnExceptionExit}
      />
    );
  }
  if (
    RegistrationStep.CREATE_CREDENTIALS === registrationStep ||
    RegistrationStep.TERMS_OF_SERVICE_REQUEST === registrationStep
  ) {
    return (
      <Registration
        initial={registrationModel.current}
        context={context}
        previous={previous}
        iamHandler={stepConfig as OnTermsAndConditionsRetrievedApi}
        submitHandler={(model: RegistrationModel) => {
          registrationModel.current = model;
        }}
      />
    );
  }
  if (RegistrationStep.EMAIL_OTP === registrationStep) {
    return (
      <EmailOtp
        context={context}
        admin={registrationModel.current.admin}
        formErrors={formError}
        submitEmailOtp={stepConfig as OnEmailOtpRequiredApi}
        previous={() => {
          setResetFlow(!resetFlow);
          setRegistrationStep(RegistrationStep.TERMS_OF_SERVICE_REQUEST);
        }}
      />
    );
  }

  log.error(
    "Registration flow: No component rendered. Registration step is probably out of sync: " +
      registrationStep,
  );

  return <></>;
};

export default RegistrationAdapter;
